/**
 * adminResourcePersonStore — Resource person slice of the admin store (Zustand).
 * Exposes ONLY the resource person endpoints (`/api/admin/resource-persons`).
 *
 * NOTE: Staff accounts are a separate concept and live in `adminStaffStore.js`
 * (`/api/admin/staff`). Do not mix the two.
 */
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import { API_URL } from "../../constants";

export const resourcePersonInitialState = {
  // List
  resourcePersons: [],
  resourcePersonsLoading: false,
  resourcePersonsError: null,
  resourcePersonsTotal: 0,
  resourcePersonsPages: 1,

  // Single resource person
  resourcePersonDetail: null,
  resourcePersonDetailLoading: false,
  resourcePersonDetailError: null,

  // Earnings
  resourcePersonEarnings: null,
  resourcePersonEarningsLoading: false,
  resourcePersonEarningsError: null,

  // Resources (courses, ebooks, etc.)
  resourcePersonResources: [],
  resourcePersonResourcesLoading: false,
  resourcePersonResourcesError: null,
  resourcePersonResourcesTotal: 0,
  resourcePersonResourcesPages: 1,
  // Raw grouped payload when the API returns { courses, books, journals, summary }
  resourcePersonResourcesData: null,

  // Students
  resourcePersonStudents: [],
  resourcePersonStudentsLoading: false,
  resourcePersonStudentsError: null,
  resourcePersonStudentsTotal: 0,
  resourcePersonStudentsPages: 1,

  // Mutation state
  resourcePersonMutationLoading: false,
  resourcePersonMutationError: null,
};

/** Normalize any list payload shape into `{ items, total, totalPages }`. */
function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items:
      payload.items ||
      payload.resourcePersons ||
      payload.resource_persons ||
      [],
    total: payload.total ?? payload.totalItems ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

export function createResourcePersonActions(set, get) {
  return {
    // ── GET /api/admin/resource-persons ──────────────────────────────────────
    fetchResourcePersons: async (params = {}) => {
      set({ resourcePersonsLoading: true, resourcePersonsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/resource-persons${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (data.error) {
          set({ resourcePersonsLoading: false, resourcePersonsError: toDisplayString(data.error, "Request failed")});
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          resourcePersonsLoading: false,
          resourcePersons: items,
          resourcePersonsTotal: total,
          resourcePersonsPages: totalPages,
        });
        return { success: true, persons: items, total, pages: totalPages };
      } catch (err) {
        set({ resourcePersonsLoading: false, resourcePersonsError: err.message });
        return { success: false, error: err.message };
      }
    },

    // ── POST /api/admin/resource-persons ─────────────────────────────────────
    createResourcePerson: async (payload) => {
      set({ resourcePersonMutationLoading: true, resourcePersonMutationError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/resource-persons`, {
          method: "POST",
          headers: authHeaders(resolveToken(get)),
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonMutationLoading: false,
            resourcePersonMutationError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        set({ resourcePersonMutationLoading: false, successMessage: "Resource person created" });
        return { success: true, person: data.data || data };
      } catch (err) {
        set({
          resourcePersonMutationLoading: false,
          resourcePersonMutationError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/resource-persons/:id ──────────────────────────────────
    fetchResourcePerson: async (id) => {
      set({ resourcePersonDetailLoading: true, resourcePersonDetailError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/resource-persons/${id}`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonDetailLoading: false,
            resourcePersonDetailError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        const person = data.data || data;
        set({ resourcePersonDetailLoading: false, resourcePersonDetail: person });
        return { success: true, person };
      } catch (err) {
        set({
          resourcePersonDetailLoading: false,
          resourcePersonDetailError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── PATCH /api/admin/resource-persons/:id ────────────────────────────────
    updateResourcePerson: async (id, payload) => {
      set({ resourcePersonMutationLoading: true, resourcePersonMutationError: null });
      try {
        // PATCH /api/admin/resource-persons/:id accepts { name?, phone?, bio?, avatar? } only.
        // email is read-only/unsupported — omit it (and any other unknown keys) to avoid
        // `Unrecognized key: "email"` / 400 errors from the API.
        const allowedKeys = ["name", "phone", "bio", "avatar"];
        const cleanPayload = {};
        for (const k of allowedKeys) {
          if (payload && payload[k] !== undefined) cleanPayload[k] = payload[k];
        }
        const response = await fetch(`${API_URL}/api/admin/resource-persons/${id}`, {
          method: "PATCH",
          headers: authHeaders(resolveToken(get)),
          body: JSON.stringify(cleanPayload),
        });
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonMutationLoading: false,
            resourcePersonMutationError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        set({ resourcePersonMutationLoading: false, successMessage: "Resource person updated" });
        return { success: true, person: data.data || data };
      } catch (err) {
        set({
          resourcePersonMutationLoading: false,
          resourcePersonMutationError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── PATCH /api/admin/resource-persons/:id/status ─────────────────────────
    updateResourcePersonStatus: async (id, status) => {
      set({ resourcePersonMutationLoading: true, resourcePersonMutationError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/resource-persons/${id}/status`, {
          method: "PATCH",
          headers: authHeaders(resolveToken(get)),
          body: JSON.stringify({ status }),
        });
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonMutationLoading: false,
            resourcePersonMutationError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        set({ resourcePersonMutationLoading: false, successMessage: "Status updated" });
        return { success: true };
      } catch (err) {
        set({
          resourcePersonMutationLoading: false,
          resourcePersonMutationError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/resource-persons/:id/earnings ─────────────────────────
    fetchResourcePersonEarnings: async (id) => {
      set({ resourcePersonEarningsLoading: true, resourcePersonEarningsError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/resource-persons/${id}/earnings`, {
          headers: authHeaders(resolveToken(get)),
        });
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonEarningsLoading: false,
            resourcePersonEarningsError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        const earnings = data.data || data;
        set({ resourcePersonEarningsLoading: false, resourcePersonEarnings: earnings });
        return { success: true, earnings };
      } catch (err) {
        set({
          resourcePersonEarningsLoading: false,
          resourcePersonEarningsError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/resource-persons/:id/resources ────────────────────────
    fetchResourcePersonResources: async (id, params = {}) => {
      set({ resourcePersonResourcesLoading: true, resourcePersonResourcesError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/resource-persons/${id}/resources${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonResourcesLoading: false,
            resourcePersonResourcesError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        // Grouped payload shape: { courses, books, journals, summary }
        const payload = data.data || data;
        if (
          payload &&
          !Array.isArray(payload) &&
          (payload.courses || payload.books || payload.journals)
        ) {
          const summary = payload.summary || {};
          const total =
            summary.totalResources ??
            (payload.courses?.length || 0) +
              (payload.books?.length || 0) +
              (payload.journals?.length || 0);
          set({
            resourcePersonResourcesLoading: false,
            resourcePersonResourcesData: payload,
            resourcePersonResources: [],
            resourcePersonResourcesTotal: total,
            resourcePersonResourcesPages: 1,
          });
          return {
            success: true,
            resources: [],
            data: payload,
            total,
            pages: 1,
          };
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          resourcePersonResourcesLoading: false,
          resourcePersonResources: items,
          resourcePersonResourcesData: null,
          resourcePersonResourcesTotal: total,
          resourcePersonResourcesPages: totalPages,
        });
        return { success: true, resources: items, total, pages: totalPages };
      } catch (err) {
        set({
          resourcePersonResourcesLoading: false,
          resourcePersonResourcesError: err.message,
        });
        return { success: false, error: err.message };
      }
    },

    // ── GET /api/admin/resource-persons/:id/students ─────────────────────────
    fetchResourcePersonStudents: async (id, params = {}) => {
      set({ resourcePersonStudentsLoading: true, resourcePersonStudentsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const response = await fetch(
          `${API_URL}/api/admin/resource-persons/${id}/students${query ? `?${query}` : ""}`,
          { headers: authHeaders(resolveToken(get)) }
        );
        const data = await response.json();
        if (data.error) {
          set({
            resourcePersonStudentsLoading: false,
            resourcePersonStudentsError: toDisplayString(data.error, "Request failed"),
          });
          return { success: false, error: toDisplayString(data.error, "Request failed")};
        }
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          resourcePersonStudentsLoading: false,
          resourcePersonStudents: items,
          resourcePersonStudentsTotal: total,
          resourcePersonStudentsPages: totalPages,
        });
        return { success: true, students: items, total, pages: totalPages };
      } catch (err) {
        set({
          resourcePersonStudentsLoading: false,
          resourcePersonStudentsError: err.message,
        });
        return { success: false, error: err.message };
      }
    },
  };
}

const resourcePersonActions = createResourcePersonActions;
export default resourcePersonActions;